import React, { useEffect, useRef, useState } from "react";
import { Camera, Send, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { log } from "../../lib/log";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ZODIAC_GLYPH, avatarSrc } from "./chatUtils";

export function ChatHeader({ kind, id, group, isCreator, title, subtitle, onAvatarUpdated }) {
  const [generating, setGenerating] = useState(false);
  const regen = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/groups/${id}/avatar/generate`, {});
      onAvatarUpdated(r.data.avatar);
      toast.success("Avatar conjured ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate avatar");
    } finally {
      setGenerating(false);
    }
  };
  return (
    <header className="px-3 sm:px-5 py-3 sm:py-4 border-b border-white/10 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <Link to="/chat" data-testid="chat-back-btn" className="md:hidden shrink-0 p-1.5 -ml-1 text-[#A89C98] hover:text-white" aria-label="Back to channels">
          <ChevronLeft size={20} />
        </Link>
        {kind === "group" && group?.avatar && (
          <img src={avatarSrc(group.avatar)} alt="" data-testid="chat-header-avatar" className="w-11 h-11 rounded-full object-cover border border-[#C8A96E]/40 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.3em] text-[#A89C98]">{subtitle}</div>
          <div className="font-heading text-xl capitalize truncate" data-testid="chat-title">{title}</div>
        </div>
      </div>
      {kind === "group" && isCreator && (
        <button data-testid="group-avatar-generate" disabled={generating} onClick={regen} className="border border-[#C8A96E]/40 text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest disabled:opacity-50">
          {generating ? "Conjuring…" : "✨ Regen Avatar"}
        </button>
      )}
    </header>
  );
}

export function renderWithMentions(text, currentUserName) {
  if (!text) return text;
  const myKey = (currentUserName || "").toLowerCase().replace(/\s+/g, "").replace(/\./g, "");
  return text.split(/(@[\w.\-]+)/g).map((p, i) => {
    if (!p.startsWith("@")) return p;
    const tok = p.slice(1).toLowerCase().replace(/\s+/g, "").replace(/\./g, "");
    const isMe = tok === myKey;
    return (
      <span key={`${i}-${p}`} data-testid="chat-mention" className={`font-bold rounded px-1 py-0.5 ${isMe ? "bg-[#C8A96E] text-[#15111A]" : "text-[#C8A96E]"}`}>
        {p}
      </span>
    );
  });
}

function ChatImage({ path }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let created = null;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((res) => { created = URL.createObjectURL(res.data); if (alive) setBlobUrl(created); })
      .catch((e) => log.warn(`Chat file thumbnail load failed for ${path}:`, e));
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!blobUrl) return <div className="w-48 h-32 bg-white/5 rounded-md animate-pulse mb-2" />;
  return <img src={blobUrl} alt="chat" className="max-w-xs rounded-md mb-2 border border-white/10" data-testid="chat-image" />;
}

export function MessageList({ messages, kind, user }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-3 sm:space-y-4" data-testid="chat-messages">
      {messages.length === 0 && <div className="text-center text-sm text-[#A89C98]">No messages yet. Send the first alignment.</div>}
      {messages.map((m) => {
        const mine = kind === "hub" ? m.user_id === user?.user_id : m.from_user_id === user?.user_id;
        const displayName = kind === "hub" ? m.user_name : m.from_name;
        const zodiac = kind === "hub" ? m.zodiac : m.from_zodiac;
        return (
          <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.message_id}`}>
            <div className={`max-w-[85%] sm:max-w-[75%] p-3 sm:p-4 ${mine ? "bg-[#4A3356] text-white rounded-2xl rounded-tr-sm" : "bg-white/10 text-white rounded-2xl rounded-tl-sm backdrop-blur-md"}`}>
              <div className={`text-xs font-bold mb-1 uppercase tracking-wider ${m.vip_lifetime ? "vip-name" : "text-[#C8A96E]"}`}>
                {displayName} {zodiac && <span className="text-[#A89C98] font-normal ml-1">· {ZODIAC_GLYPH[zodiac]} {zodiac}</span>}
              </div>
              {m.image_path && <ChatImage path={m.image_path} />}
              {m.text && <div className="text-sm whitespace-pre-wrap break-words">{renderWithMentions(m.text, user?.name)}</div>}
              <div className="text-[10px] text-[#A89C98] mt-2">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

export function Composer({ text, setText, onSend, onUpload, uploading }) {
  const fileInputRef = useRef(null);
  return (
    <div className="p-3 sm:p-4 border-t border-white/10 flex items-center gap-2">
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onUpload} data-testid="chat-file-input" />
      <button data-testid="chat-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-10 h-10 rounded-full glass-gold flex items-center justify-center text-[#C8A96E] hover:bg-white/10">
        <Camera size={16} />
      </button>
      <Input
        data-testid="chat-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), onSend())}
        placeholder={uploading ? "Uploading…" : "Type a message…"}
        className="flex-1 bg-transparent border-b border-[#A89C98]/30 rounded-none h-10 text-white"
      />
      <Button data-testid="chat-send-btn" onClick={() => onSend()} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-4">
        <Send size={16} />
      </Button>
    </div>
  );
}
